import type { ILayerProps } from '@/version-types';
import { getMoveState } from './active/states/move-state';
import { CLEAR_MOUSE_OPERATE } from './event-name';
import { MouseHover } from './hover/hover';
import type {
    MouseOperateEventListener,
    MoveAfterUpdateEvent,
    MoveBeforeUpdateEvent,
    MoveEndEvent,
    MoveStartEvent,
    ResizeAfterUpdateEvent,
    ResizeBeforeUpdateEvent,
    ResizeEndEvent,
    ResizeStartEvent,
    RotateAfterUpdateEvent,
    RotateBeforeUpdateEvent,
    RotateEndEvent,
    RotateStartEvent,
    ScaleAfterUpdateEvent,
    ScaleBeforeUpdateEvent,
    ScaleEndEvent,
    ScaleStartEvent,
} from './operation-events';

/**
 * MouseOperate 实例内部使用的同步生命周期事件名称。
 * 跨插件只能通过 MouseOperate 的 on* 方法订阅，不能直接依赖这些字符串。
 */
const MOUSE_OPERATE_EVENT_NAMES = {
    CANCEL: 'mouse-operate:cancel',
    MOVE_START: 'mouse-operate:move:start',
    MOVE_BEFORE_UPDATE: 'mouse-operate:move:before-update',
    MOVE_AFTER_UPDATE: 'mouse-operate:move:after-update',
    MOVE_END: 'mouse-operate:move:end',
    RESIZE_START: 'mouse-operate:resize:start',
    RESIZE_BEFORE_UPDATE: 'mouse-operate:resize:before-update',
    RESIZE_AFTER_UPDATE: 'mouse-operate:resize:after-update',
    RESIZE_END: 'mouse-operate:resize:end',
    SCALE_START: 'mouse-operate:scale:start',
    SCALE_BEFORE_UPDATE: 'mouse-operate:scale:before-update',
    SCALE_AFTER_UPDATE: 'mouse-operate:scale:after-update',
    SCALE_END: 'mouse-operate:scale:end',
    ROTATE_START: 'mouse-operate:rotate:start',
    ROTATE_BEFORE_UPDATE: 'mouse-operate:rotate:before-update',
    ROTATE_AFTER_UPDATE: 'mouse-operate:rotate:after-update',
    ROTATE_END: 'mouse-operate:rotate:end',
} as const;

/** 同一编辑器中互斥的四类基础操作。 */
type BaseOperationType = 'move' | 'resize' | 'scale' | 'rotate';

/** start 到 end/cancel 期间用于保证结束事件互斥的实例级标记。 */
interface ActiveOperation {
    /** 当前基础操作类型。 */
    type: BaseOperationType;
    /** start 时的原始操作图层 ID。 */
    layerId: string;
}

/**
 * 鼠标基础操作插件实例。
 *
 * 除公开配置外，本实例是 move/resize/scale/rotate 生命周期事件的唯一入口。
 * 跨插件通过 registration.getInstance 获取本实例并调用 on* 方法。
 */
export class MouseOperate extends MouseHover {
    /** 图片图层顶部旋转手柄的纵向偏移。 */
    imageLayerRotateOffsetY = '150%';
    /** 框选区域描边颜色。 */
    strokeColor = 'rgba(255, 255, 255, 1)';
    /** 框选区域填充颜色。 */
    fillColor = 'rgba(255, 255, 255, 0.5)';
    /** 图片图层旋转手柄的默认纵向偏移。 */
    defaultImageLayerRotateOffsetY = '270%';

    /**
     * 当前编辑器实例正在进行的基础操作。
     * 仅用于保证正常 end 与 cancel 互斥，不参与几何、history 或 sticky 计算。
     */
    private activeOperation?: ActiveOperation;

    /**
     * 解析本帧实际操作图层。
     * 保持重构前 proxy 的 active pick → active layer 回退规则，返回值可能与原 layerId 不同。
     */
    resolveCurrentOperateLayer(): ILayerProps | null {
        const activeInfo = this.framework.service.frame.getActiveInfo();
        let layer: ILayerProps | null = null;

        if (activeInfo?.info?.pickId) {
            layer = this.framework.service.flatLayer.getLayer(activeInfo.info.pickId);
            if (this.framework.service.flatLayer.checkDisableChildBaseOperate(layer)) {
                layer = null;
            }
        }
        return layer || this.framework.service.frame.getActiveLayer();
    }

    /** 监听非正常中断；返回函数用于解绑当前编辑器实例的监听器。 */
    onCancel(listener: MouseOperateEventListener<void>) {
        return this.subscribe(MOUSE_OPERATE_EVENT_NAMES.CANCEL, listener);
    }

    /** 监听 move start；返回函数用于解绑。 */
    onMoveStart(listener: MouseOperateEventListener<MoveStartEvent>) {
        return this.subscribe(MOUSE_OPERATE_EVENT_NAMES.MOVE_START, listener);
    }

    /** 监听 move 基础更新前的同步候选阶段；返回函数用于解绑。 */
    onMoveBeforeUpdate(listener: MouseOperateEventListener<MoveBeforeUpdateEvent>) {
        return this.subscribe(MOUSE_OPERATE_EVENT_NAMES.MOVE_BEFORE_UPDATE, listener);
    }

    /** 监听 move 基础更新成功后的阶段；即使最新 layer 缺失也会触发。 */
    onMoveAfterUpdate(listener: MouseOperateEventListener<MoveAfterUpdateEvent>) {
        return this.subscribe(MOUSE_OPERATE_EVENT_NAMES.MOVE_AFTER_UPDATE, listener);
    }

    /** 监听 move 正常结束；与 cancel 互斥。 */
    onMoveEnd(listener: MouseOperateEventListener<MoveEndEvent>) {
        return this.subscribe(MOUSE_OPERATE_EVENT_NAMES.MOVE_END, listener);
    }

    /** 监听 resize start；首帧仍只初始化。 */
    onResizeStart(listener: MouseOperateEventListener<ResizeStartEvent>) {
        return this.subscribe(MOUSE_OPERATE_EVENT_NAMES.RESIZE_START, listener);
    }

    /** 监听 resize 基础更新前的同步候选阶段。 */
    onResizeBeforeUpdate(listener: MouseOperateEventListener<ResizeBeforeUpdateEvent>) {
        return this.subscribe(MOUSE_OPERATE_EVENT_NAMES.RESIZE_BEFORE_UPDATE, listener);
    }

    /** 监听 resize 基础更新成功后的尺寸结果。 */
    onResizeAfterUpdate(listener: MouseOperateEventListener<ResizeAfterUpdateEvent>) {
        return this.subscribe(MOUSE_OPERATE_EVENT_NAMES.RESIZE_AFTER_UPDATE, listener);
    }

    /** 监听 resize 原 cursor tip 同步清理边界；不等待异步 history。 */
    onResizeEnd(listener: MouseOperateEventListener<ResizeEndEvent>) {
        return this.subscribe(MOUSE_OPERATE_EVENT_NAMES.RESIZE_END, listener);
    }

    /** 监听 scale start；首帧仍只初始化。 */
    onScaleStart(listener: MouseOperateEventListener<ScaleStartEvent>) {
        return this.subscribe(MOUSE_OPERATE_EVENT_NAMES.SCALE_START, listener);
    }

    /** 监听 scale 基础更新前阶段；当前不开放吸附候选。 */
    onScaleBeforeUpdate(listener: MouseOperateEventListener<ScaleBeforeUpdateEvent>) {
        return this.subscribe(MOUSE_OPERATE_EVENT_NAMES.SCALE_BEFORE_UPDATE, listener);
    }

    /** 监听 scale 基础更新成功后的尺寸结果。 */
    onScaleAfterUpdate(listener: MouseOperateEventListener<ScaleAfterUpdateEvent>) {
        return this.subscribe(MOUSE_OPERATE_EVENT_NAMES.SCALE_AFTER_UPDATE, listener);
    }

    /** 监听 scale 原 cursor tip 同步清理边界；不等待异步 history。 */
    onScaleEnd(listener: MouseOperateEventListener<ScaleEndEvent>) {
        return this.subscribe(MOUSE_OPERATE_EVENT_NAMES.SCALE_END, listener);
    }

    /** 监听 rotate start；rotate 首帧仍继续更新角度。 */
    onRotateStart(listener: MouseOperateEventListener<RotateStartEvent>) {
        return this.subscribe(MOUSE_OPERATE_EVENT_NAMES.ROTATE_START, listener);
    }

    /** 监听 rotate 写入角度前的原始角度。 */
    onRotateBeforeUpdate(listener: MouseOperateEventListener<RotateBeforeUpdateEvent>) {
        return this.subscribe(MOUSE_OPERATE_EVENT_NAMES.ROTATE_BEFORE_UPDATE, listener);
    }

    /** 监听 rotate 更新成功后的吸附角度。 */
    onRotateAfterUpdate(listener: MouseOperateEventListener<RotateAfterUpdateEvent>) {
        return this.subscribe(MOUSE_OPERATE_EVENT_NAMES.ROTATE_AFTER_UPDATE, listener);
    }

    /** 监听 rotate 原 cursor tip 同步清理边界。 */
    onRotateEnd(listener: MouseOperateEventListener<RotateEndEvent>) {
        return this.subscribe(MOUSE_OPERATE_EVENT_NAMES.ROTATE_END, listener);
    }

    /** 发送 move start 并建立本次活动生命周期。 */
    emitMoveStart(event: MoveStartEvent) {
        this.emitStart('move', MOUSE_OPERATE_EVENT_NAMES.MOVE_START, event);
    }

    /** 同步发送 move beforeUpdate。 */
    emitMoveBeforeUpdate(event: MoveBeforeUpdateEvent) {
        this.emit(MOUSE_OPERATE_EVENT_NAMES.MOVE_BEFORE_UPDATE, event);
    }

    /** 在 updateLayerPosition 成功返回后无条件发送。 */
    emitMoveAfterUpdate(event: MoveAfterUpdateEvent) {
        this.emit(MOUSE_OPERATE_EVENT_NAMES.MOVE_AFTER_UPDATE, event);
    }

    /** 发送 move end 并关闭本次生命周期。 */
    emitMoveEnd(event: MoveEndEvent) {
        this.emitEnd('move', MOUSE_OPERATE_EVENT_NAMES.MOVE_END, event);
    }

    /** 发送 resize start 并建立本次活动生命周期。 */
    emitResizeStart(event: ResizeStartEvent) {
        this.emitStart('resize', MOUSE_OPERATE_EVENT_NAMES.RESIZE_START, event);
    }

    /** 同步发送 resize beforeUpdate。 */
    emitResizeBeforeUpdate(event: ResizeBeforeUpdateEvent) {
        this.emit(MOUSE_OPERATE_EVENT_NAMES.RESIZE_BEFORE_UPDATE, event);
    }

    /** 在 updateLayerResize 成功返回后无条件发送。 */
    emitResizeAfterUpdate(event: ResizeAfterUpdateEvent) {
        this.emit(MOUSE_OPERATE_EVENT_NAMES.RESIZE_AFTER_UPDATE, event);
    }

    /** 发送 resize end 并关闭本次生命周期。 */
    emitResizeEnd(event: ResizeEndEvent) {
        this.emitEnd('resize', MOUSE_OPERATE_EVENT_NAMES.RESIZE_END, event);
    }

    /** 发送 scale start 并建立本次活动生命周期。 */
    emitScaleStart(event: ScaleStartEvent) {
        this.emitStart('scale', MOUSE_OPERATE_EVENT_NAMES.SCALE_START, event);
    }

    /** 同步发送 scale beforeUpdate。 */
    emitScaleBeforeUpdate(event: ScaleBeforeUpdateEvent) {
        this.emit(MOUSE_OPERATE_EVENT_NAMES.SCALE_BEFORE_UPDATE, event);
    }

    /** 在 updateLayerScale 成功返回后无条件发送。 */
    emitScaleAfterUpdate(event: ScaleAfterUpdateEvent) {
        this.emit(MOUSE_OPERATE_EVENT_NAMES.SCALE_AFTER_UPDATE, event);
    }

    /** 发送 scale end 并关闭本次生命周期。 */
    emitScaleEnd(event: ScaleEndEvent) {
        this.emitEnd('scale', MOUSE_OPERATE_EVENT_NAMES.SCALE_END, event);
    }

    /** 发送 rotate start 并建立本次活动生命周期。 */
    emitRotateStart(event: RotateStartEvent) {
        this.emitStart('rotate', MOUSE_OPERATE_EVENT_NAMES.ROTATE_START, event);
    }

    /** 同步发送 rotate beforeUpdate。 */
    emitRotateBeforeUpdate(event: RotateBeforeUpdateEvent) {
        this.emit(MOUSE_OPERATE_EVENT_NAMES.ROTATE_BEFORE_UPDATE, event);
    }

    /** 在 updateLayerRotate 成功返回后无条件发送。 */
    emitRotateAfterUpdate(event: RotateAfterUpdateEvent) {
        this.emit(MOUSE_OPERATE_EVENT_NAMES.ROTATE_AFTER_UPDATE, event);
    }

    /** 发送 rotate end 并关闭本次生命周期。 */
    emitRotateEnd(event: RotateEndEvent) {
        this.emitEnd('rotate', MOUSE_OPERATE_EVENT_NAMES.ROTATE_END, event);
    }

    /**
     * 发送一次非正常中断并清除活动标记。
     * 重复调用或正常 end 后调用不会再次通知消费插件。
     */
    emitCancel() {
        if (!this.activeOperation) return;
        this.activeOperation = undefined;
        this.emit(MOUSE_OPERATE_EVENT_NAMES.CANCEL, undefined);
    }

    /**
     * 请求框选模块清理当前临时操作。
     * 键盘移动等外部功能通过实例调用，避免依赖 mouse-operate 内部事件常量。
     */
    clearSelectionOperate() {
        this.framework.service.events.emit(CLEAR_MOUSE_OPERATE);
    }

    /**
     * 设置框选颜色
     * @param params - 参数对象
     */
    setSelectColor({ strokeColor, fillColor }: { strokeColor?: string; fillColor?: string } = {}) {
        this.strokeColor = strokeColor || this.strokeColor;
        this.fillColor = fillColor || this.fillColor;
    }

    /**
     * 设置通用 active 操作框是否渲染
     *
     * @param enabled - 是否渲染通用 active 操作框
     *
     * 副作用：更新当前编辑器实例的鼠标操作 Zustand 状态。
     */
    setActiveRenderEnabled(enabled: boolean) {
        getMoveState(this.framework.identity).setActiveRenderEnabled(enabled);
    }

    /** 设置图片图层旋转手柄的纵向偏移。 */
    setImageLayerRotateOffsetY(offsetY: string) {
        this.imageLayerRotateOffsetY = offsetY;
    }

    /** 封装无类型事件总线，并避免 listener 返回 true 中断后续插件监听器。 */
    private subscribe<T>(eventName: string, listener: MouseOperateEventListener<T>) {
        const handler = this.framework.service.events.on(eventName, (event: T) => {
            listener(event);
        });
        return () => this.framework.service.events.off(handler);
    }

    /** 同步发送实例内部事件；监听器错误保持向上传播。 */
    private emit<T>(eventName: string, event: T) {
        this.framework.service.events.emit(eventName, event);
    }

    /** 新操作开始前先取消仍未结束的旧操作，再建立新的活动标记。 */
    private emitStart<T extends { layerId: string }>(type: BaseOperationType, eventName: string, event: T) {
        this.emitCancel();
        this.activeOperation = { type, layerId: event.layerId };
        this.emit(eventName, event);
    }

    /** 正常 end 先清除活动标记，保证 end 监听期间触发的 cancel 不会重复分发。 */
    private emitEnd<T extends { layerId: string }>(type: BaseOperationType, eventName: string, event: T) {
        const active = this.activeOperation;
        if (!active || active.type !== type || active.layerId !== event.layerId) return;
        this.activeOperation = undefined;
        this.emit(eventName, event);
    }
}
